import Vue from 'vue'
import ResizeObserver from 'resize-observer-polyfill'

const isServer = Vue.prototype.$isServer
const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g
const MOZ_HACK_REGEXP = /^moz([A-Z])/
const ieVersion = isServer ? 0 : Number(document.documentMode)

/* istanbul ignore next */
const resizeHandler = function (entries) {
  for (let entry of entries) {
    const listeners = entry.target.__resizeListeners__ || []
    if (listeners.length) {
      listeners.forEach((fn) => {
        fn()
      })
    }
  }
}

/* istanbul ignore next */
export const addResizeListener = function (element, fn) {
  if (isServer) return
  if (!element.__resizeListeners__) {
    element.__resizeListeners__ = []
    element.__ro__ = new ResizeObserver(resizeHandler)
    element.__ro__.observe(element)
  }
  element.__resizeListeners__.push(fn)
}

/* istanbul ignore next */
export const removeResizeListener = function (element, fn) {
  if (!element || !element.__resizeListeners__) return
  element.__resizeListeners__.splice(element.__resizeListeners__.indexOf(fn), 1)
  if (!element.__resizeListeners__.length) {
    element.__ro__.disconnect()
  }
}

/* istanbul ignore next */
const trim = function (string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '')
}
/* istanbul ignore next */
const camelCase = function (name) {
  return name
    .replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
      return offset ? letter.toUpperCase() : letter
    })
    .replace(MOZ_HACK_REGEXP, 'Moz$1')
}

/* istanbul ignore next */
export const on = (function () {
  if (!isServer && document.addEventListener) {
    return function (el, event, handler) {
      if (el && event && handler) {
        el.addEventListener(event, handler, false)
      }
    }
  } else {
    return function (el, event, handler) {
      if (el && event && handler) {
        el.attachEvent('on' + event, handler)
      }
    }
  }
})()

/* istanbul ignore next */
export const off = (function () {
  if (!isServer && document.removeEventListener) {
    return function (el, event, handler) {
      if (el && event) {
        el.removeEventListener(event, handler, false)
      }
    }
  } else {
    return function (el, event, handler) {
      if (el && event) {
        el.detachEvent('on' + event, handler)
      }
    }
  }
})()

/* istanbul ignore next */
export const once = function (el, event, fn) {
  var listener = function () {
    if (fn) {
      fn.apply(this, arguments)
    }
    off(el, event, listener)
  }
  on(el, event, listener)
}

/**
 * @description: 判断元素是否拥有某个class
 * @param el 指定元素
 * @param cls class字符串
 * @return: Boolean
 */
export function hasClass(el, cls) {
  if (!el || !cls) return false
  if (cls.indexOf(' ') !== -1) throw new Error('className should not contain space.')
  if (el.classList) {
    return el.classList.contains(cls)
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1
  }
}

/**
 * @description: 给元素加上某个class
 * @param el 指定元素
 * @param cls class字符串
 */
export function addClass(el, cls) {
  if (!el) return
  var curClass = el.className
  var classes = (cls || '').split(' ')

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i]
    if (!clsName) continue

    if (el.classList) {
      el.classList.add(clsName)
    } else if (!hasClass(el, clsName)) {
      curClass += ' ' + clsName
    }
  }
  if (!el.classList) {
    el.className = curClass
  }
}

/**
 * @description: 给元素加上某个class
 * @param el 指定元素
 * @param cls class字符串
 */
export function removeClass(el, cls) {
  if (!el || !cls) return
  var classes = cls.split(' ')
  var curClass = ' ' + el.className + ' '

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i]
    if (!clsName) continue

    if (el.classList) {
      el.classList.remove(clsName)
    } else if (hasClass(el, clsName)) {
      curClass = curClass.replace(' ' + clsName + ' ', ' ')
    }
  }
  if (!el.classList) {
    el.className = trim(curClass)
  }
}

/**
 * @description: 获取指定元素的某个样式值
 * @param el 指定元素
 * @param styleName 样式名称
 * @return: String
 */
export const getStyle =
  ieVersion < 9
    ? function (el, styleName) {
        if (isServer) return
        if (!el || !styleName) return null
        styleName = camelCase(styleName)
        if (styleName === 'float') {
          styleName = 'styleFloat'
        }
        try {
          switch (styleName) {
            case 'opacity':
              try {
                return el.filters.item('alpha').opacity / 100
              } catch (e) {
                return 1.0
              }
            default:
              return el.style[styleName] || el.currentStyle ? el.currentStyle[styleName] : null
          }
        } catch (e) {
          return el.style[styleName]
        }
      }
    : function (el, styleName) {
        if (isServer) return
        if (!el || !styleName) return null
        styleName = camelCase(styleName)
        if (styleName === 'float') {
          styleName = 'cssFloat'
        }
        try {
          if (['height', 'width'].includes(styleName)) {
            var rect = el.getBoundingClientRect()
            return rect[styleName].toString()
          }
          var computed = document.defaultView.getComputedStyle(el, '')
          return el.style[styleName] || computed ? computed[styleName] : null
        } catch (e) {
          return el.style[styleName]
        }
      }

/**
 * @description: 设置指定元素的某个样式值
 * @param el 指定元素
 * @param styleName 样式名称
 * @param value 样式值
 */
export function setStyle(el, styleName, value) {
  if (!el || !styleName) return

  if (typeof styleName === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(el, prop, styleName[prop])
      }
    }
  } else {
    styleName = camelCase(styleName)
    if (styleName === 'opacity' && ieVersion < 9) {
      el.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')'
    } else {
      el.style[styleName] = value
    }
  }
}

export const getWidth = (elem) => {
  let width = elem && typeof elem.getBoundingClientRect === 'function' && elem.getBoundingClientRect().width
  if (width) {
    width = +width.toFixed(6)
  }
  return width || 0
}

export const getHeight = (elem) => {
  let height = elem && typeof elem.getBoundingClientRect === 'function' && elem.getBoundingClientRect().height
  if (height) {
    height = +height.toFixed(6)
  }
  return height || 0
}

/**
 * @description: 判断元素是否可以滚动
 * @param el 指定元素
 * @param vertical true:竖向滚动条,false:横向滚动条,不传时，两个滚动条
 * @return: Boolean
 */
export function isScroll(el, vertical) {
  if (isServer) return

  const determinedDirection = vertical !== null || vertical !== undefined
  const overflow = determinedDirection ? (vertical ? getStyle(el, 'overflow-y') : getStyle(el, 'overflow-x')) : getStyle(el, 'overflow')

  return overflow.match(/(scroll|auto)/)
}

/**
 * @description: 获取指定元素的滚动父节点。向上递归。
 * @param el 指定元素
 * @param vertical true:竖向滚动条,false:横向滚动条,不传时，两个滚动条
 * @return: dom元素
 */
export function getScrollContainer(el, vertical) {
  if (isServer) return

  let parent = el
  while (parent) {
    if ([window, document, document.documentElement].includes(parent)) {
      return window
    }
    if (isScroll(parent, vertical)) {
      return parent
    }
    parent = parent.parentNode
  }

  return parent
}

/**
 * @description: 判断元素是否在指定容器内。不判断父子关系。
 * @param el 指定元素
 * @param container 指定容器
 * @return: Boolean
 */
export function isInContainer(el, container) {
  if (isServer || !el || !container) return false

  const elRect = el.getBoundingClientRect()
  let containerRect

  if ([window, document, document.documentElement, null, undefined].includes(container)) {
    containerRect = {
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0
    }
  } else {
    containerRect = container.getBoundingClientRect()
  }

  return (
    elRect.top < containerRect.bottom &&
    elRect.bottom > containerRect.top &&
    elRect.right > containerRect.left &&
    elRect.left < containerRect.right
  )
}

/**
 * @description: 根据指定元素向上查找vue组件
 * @param element dom元素
 * @param componentName vue组件名。
 * @return: vm
 */
export function findNearestComponent(element, componentName) {
  let target = element
  while (target && target.tagName !== 'BODY') {
    if (target.__vue__ && target.__vue__.$options.name === componentName) {
      return target.__vue__
    }
    target = target.parentNode
  }
  return null
}

/**
 * @description: 将像素单位转化为数字
 * @param px
 * @return: number
 */
export function pxToNum(px) {
  if (!px) {
    return 0
  }
  let num = Number(px.replace(/[^\d]/g, ''))
  if (px.charAt(0) === '-') {
    num = 0 - num
  }
  return num
}

/**
 * @description: 获取指定元素的外边距。
 * @param el
 * @param der 宽或者高
 * @return: number
 */
export function getElMargin(el, der = 'height') {
  if (!el.clientHeight) return 0
  let margin = 0
  if (der === 'width') {
    margin = pxToNum(getStyle(el, 'marginLeft')) + pxToNum(getStyle(el, 'marginRight'))
  } else {
    margin = pxToNum(getStyle(el, 'marginBottom')) + pxToNum(getStyle(el, 'marginTop'))
  }
  return margin
}

/**
 * @description: 获取指定元素的内边距
 * @param el
 * @param der 宽或者高
 * @return: number
 */
export function getElPadding(el, der = 'height') {
  if (!el.clientHeight) return 0
  let padding = 0
  if (der === 'width') {
    padding = pxToNum(getStyle(el, 'paddingLeft')) + pxToNum(getStyle(el, 'paddingRight'))
  } else {
    padding = pxToNum(getStyle(el, 'paddingBottom')) + pxToNum(getStyle(el, 'paddingTop'))
  }
  return padding
}

/**
 * @description: 获取指定元素，相对于上级元素的最大高度。
 * @param el
 * @param parentElClass 上级元素的class
 * @return: number
 */
export function calcEleFitHeight(el, parentElClass) {
  let parent = el.parentElement
  let calcEl = el
  let height = 0
  while (parent) {
    // 减去本身的margin
    height -= getElMargin(el) + (calcEl === el ? 0 : getElPadding(el))
    parent.childNodes.forEach((child) => {
      if (child !== el && child.clientHeight) {
        let position = getStyle(child, 'position')
        // 绝对定位的元素，不需要计算高宽度。
        if (['absolute', 'fixed'].indexOf(position) === -1) {
          // 减去其他元素的高度和margin
          height -= child.clientHeight + getElMargin(child)
        }
      }
    })
    if (!hasClass(parent, parentElClass)) {
      el = parent
      parent = parent.parentElement
    } else {
      let padding = getElPadding(parent)
      height += parent.clientHeight - padding
      break
    }
  }
  return height
}



// WEBPACK FOOTER //
// ./src/components/SimbartUI/utils/dom.js